import { supabase } from '@/integrations/supabase/client';

export interface PVSettings {
  redirect_url: string;
  total_clicks: number;
  total_conversions: number;
  updated_at: string;
}

export async function fetchPVSettings(): Promise<PVSettings> {
  const { data, error } = await supabase
    .from('pv_settings' as any)
    .select('redirect_url, total_clicks, total_conversions, updated_at')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return data as any;
}

export async function fetchPVRedirectUrl(): Promise<string> {
  const { data, error } = await supabase
    .from('pv_settings' as any)
    .select('redirect_url')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return (data as any)?.redirect_url ?? '';
}

export async function updatePVRedirectUrl(url: string): Promise<void> {
  const { error } = await supabase
    .from('pv_settings' as any)
    .update({ redirect_url: url, updated_at: new Date().toISOString() } as any)
    .eq('id', 1);
  if (error) throw error;
}

export async function trackPVClick(source: string): Promise<void> {
  const { error } = await supabase.rpc('track_pv_click', { p_source: source } as any);
  if (error) console.error('[PV] click track error:', error);
}

export interface PVClick {
  id: string;
  user_id: string;
  source: string;
  created_at: string;
}

export async function fetchPVClicks(limit = 50): Promise<PVClick[]> {
  const { data, error } = await supabase
    .from('pv_clicks' as any)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as any;
}

export interface PVConversion {
  id: string;
  user_id: string;
  converted_at: string;
  display_name?: string | null;
}

export async function fetchPVConversions(limit = 50): Promise<PVConversion[]> {
  const { data, error } = await supabase
    .from('pv_conversions' as any)
    .select('*')
    .order('converted_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  const conversions = (data ?? []) as any as PVConversion[];

  const userIds = [...new Set(conversions.map(c => c.user_id))];
  if (userIds.length === 0) return conversions;

  const { data: profiles } = await supabase
    .from('profiles')
    .select('user_id, display_name, full_name')
    .in('user_id', userIds);

  const profileMap = new Map(
    (profiles ?? []).map((p: any) => [p.user_id, p.display_name || p.full_name])
  );

  return conversions.map(c => ({
    ...c,
    display_name: profileMap.get(c.user_id) ?? null,
  }));
}

export async function resetPVMetrics(): Promise<void> {
  const { error } = await supabase.rpc('reset_pv_metrics' as any);
  if (error) throw error;
}
